import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { crudStatusEnum, getPriceTypeLabel, priceTypeEnum, systemConfigKeyEnum } from '../../../../constants';
import { useCreateUtilityVehicleMutation, useUpdateUtilityVehicleMutation } from '../../../../hooks/mutations/useUtilityVehicleMutation';
import { useUsersQuery } from '../../../../hooks/queries/useUsersQuery';
import { useVehicleTypesQuery } from '../../../../hooks/queries/useVehiclesTypesQuery';
import { useAuth } from '../../../../hooks/useAuth';
import { useSystemConfig } from '../../../../hooks/useSystemConfig';
import { UtilityVehicle } from '../../../../services/apiSLTP/materials/utilityVehicles';
import { capitalizeFirstLetter, formatName } from '../../../../utils/capitalize';
import CustomTextInput from '../../../ui/CustomTextInput';
import DropDown, { OptionType } from '../../../ui/DropDown';
import ToggleLine from '../../../ui/ToggleLine';
import ModalBody from '../../ModalBody';

interface ModalProps {
    utilityVehicle: UtilityVehicle | null;
    showEditModal: boolean;
    isEditMode?: boolean;
    setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>;
    refetch: () => void;
}

const UpdateUtilityVehicleModal: React.FC<ModalProps> = ({ utilityVehicle, showEditModal, setShowEditModal, isEditMode = true, refetch }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { getConfigValue } = useSystemConfig();

    const [selectedUser, setSelectedUser] = useState<OptionType | null>(null);
    const [selectedType, setSelectedType] = useState<OptionType | null>(null);
    const [priceType, setPriceType] = useState<OptionType | null>(
        utilityVehicle?.priceType
            ? {
                  label: capitalizeFirstLetter(t(getPriceTypeLabel(utilityVehicle?.priceType))),
                  value: (utilityVehicle?.priceType).toString(),
              }
            : null
    );

    const [selectedDesignation, setSelectedDesignation] = useState(utilityVehicle?.designation || '');
    const [selectedCodeProC, setSelectedCodeProC] = useState(utilityVehicle?.codeProC || '');
    const [selectedPlateNumber, setSelectedPlateNumber] = useState(utilityVehicle?.plateNumber || '');
    const [selectedCrudStatus, setSelectedCrudStatus] = useState(utilityVehicle?.crudStatus || crudStatusEnum.OK);
    const [price, setPrice] = useState(typeof utilityVehicle?.price === 'number' ? utilityVehicle?.price.toString() : '');
    const [baseName, setBaseName] = useState(utilityVehicle?.baseName || '');

    const { data: dataUsers, isLoading: isUsersLoading, refetch: refetchUsers } = useUsersQuery({});
    const { data: dataVehiclesTypes, isLoading: isVehiclesTypesLoading, refetch: refetchVehiclesTypes } = useVehicleTypesQuery({});

    const { mutate: mutateUtilityVehicle } = useUpdateUtilityVehicleMutation({
        onSuccess: refetch,
        onError: (error) => console.error('Error updating utilityVehicle:', error.message),
    });

    const { mutate: addUtilityVehicle } = useCreateUtilityVehicleMutation({
        onSuccess: refetch,
        onError: (error) => console.error('Error adding utilityVehicle:', error.message),
    });

    useEffect(() => {
        if (utilityVehicle) {
            const user = dataUsers?.find((u) => u._id === utilityVehicle.userId?._id);
            if (user) setSelectedUser({ label: formatName(user.firstName, user.lastName), value: user._id });
        }
    }, [dataUsers]);

    useEffect(() => {
        if (utilityVehicle) {
            const type = dataVehiclesTypes?.find((u) => u._id === utilityVehicle.vehicleTypeId?._id);
            if (type) setSelectedType({ label: type.designation, value: type._id });
        }
    }, [dataVehiclesTypes]);

    const handleActiveChange = (isActive: boolean) => {
        setSelectedCrudStatus(isActive ? crudStatusEnum.OK : crudStatusEnum.INACTIVE);
    };

    const isFormValid = () => {
        return selectedDesignation && selectedCodeProC && selectedPlateNumber && selectedType && baseName;
    };

    const handleSubmit = () => {
        if (isEditMode) {
            mutateUtilityVehicle({
                utilityVehicleId: utilityVehicle?._id || '',
                data: {
                    designation: selectedDesignation,
                    codeProC: selectedCodeProC,
                    crudStatus: selectedCrudStatus,
                    plateNumber: selectedPlateNumber,
                    userId: selectedUser?.value || null,
                    vehicleTypeId: selectedType?.value,
                    priceType: priceType ? parseInt(priceType.value) : null,
                    price: parseFloat(price),
                    baseName,
                },
            });
        } else {
            if (selectedType)
                addUtilityVehicle({
                    designation: selectedDesignation,
                    codeProC: selectedCodeProC,
                    crudStatus: selectedCrudStatus,
                    plateNumber: selectedPlateNumber,
                    userId: selectedUser?.value || null,
                    vehicleTypeId: selectedType?.value,
                    priceType: priceType ? parseInt(priceType.value) : null,
                    price: parseFloat(price),
                    baseName,
                });
        }
    };

    return (
        <ModalBody
            title={t('vehicle')}
            subtitle={isEditMode ? t('update') : t('add')}
            disableSubmit={!isFormValid()}
            open={showEditModal}
            handleClose={() => setShowEditModal(false)}
            onSubmit={handleSubmit}
        >
            <CustomTextInput value={selectedDesignation} onChange={setSelectedDesignation} label={t('name')} required />
            <CustomTextInput value={selectedCodeProC} onChange={setSelectedCodeProC} label={t('codeProC')} required />
            <ToggleLine checked={selectedCrudStatus === crudStatusEnum.OK} onChange={handleActiveChange} title={capitalizeFirstLetter(t('active'))} />
            <CustomTextInput value={selectedPlateNumber} onChange={setSelectedPlateNumber} label={t('plateNumber')} required />
            <DropDown
                items={dataVehiclesTypes?.map((type) => ({ label: type.designation, value: type._id })) || []}
                selectedItem={selectedType}
                handleChange={(e, value) => setSelectedType(value)}
                inputLabel={t('type')}
                required
                loading={isVehiclesTypesLoading}
            />
            <DropDown
                items={dataUsers?.map((user) => ({ label: formatName(user.firstName, user.lastName), value: user._id })) || []}
                selectedItem={selectedUser}
                handleChange={(e, value) => setSelectedUser(value)}
                inputLabel={t('driver')}
                disableClearable={false}
                loading={isUsersLoading}
            />
            <CustomTextInput
                value={price}
                onChange={setPrice}
                label={t('price')}
                errorText={t('error')}
                startAdornment={<div>{getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)}</div>}
                type="number"
            />
            <DropDown
                items={Object.values(priceTypeEnum).map((type) => ({
                    label: capitalizeFirstLetter(t(getPriceTypeLabel(type))),
                    value: type.toString(),
                }))}
                selectedItem={priceType}
                handleChange={(e, value) => {
                    setPriceType(value);
                }}
                inputLabel={t('pricing')}
                disableClearable={false}
            />
            <CustomTextInput value={baseName} onChange={setBaseName} label={t('baseName')} required />
        </ModalBody>
    );
};

export default UpdateUtilityVehicleModal;
